// API Service Layer for SurveyPro ERP
// Replace mock data with real API calls

// API base URL must be set via VITE_API_URL in your .env file (e.g. VITE_API_URL=https://yourdomain.com/api)
// Falls back to relative /api for same-origin deployments (nginx proxy)
const rawApiUrl = import.meta.env.VITE_API_URL;
const API_BASE_URL = typeof rawApiUrl === 'string' && rawApiUrl.startsWith('http') 
  ? rawApiUrl 
  : '/api';

// Debug: Log API URL in development
if (import.meta.env.DEV) {
  console.log('[API] Base URL:', API_BASE_URL);
}

// Helper to get auth token
const getToken = () => localStorage.getItem('token');

// ── Request Deduplication ─────────────────────────────────────────────────────
// Prevents duplicate identical GET requests from being sent simultaneously
const pendingRequests = new Map<string, Promise<unknown>>();

function getRequestKey(endpoint: string, options: Parameters<typeof fetch>[1] = {}): string {
  const method = options.method || 'GET';
  const body = options.body ? String(options.body) : '';
  return `${method}:${endpoint}:${body}`;
}

function dedupeRequest<T>(endpoint: string, options: Parameters<typeof fetch>[1] = {}, fetchFn: () => Promise<T>): Promise<T> {
  const key = getRequestKey(endpoint, options);
  
  // Only dedupe GET requests
  if ((options.method || 'GET') !== 'GET') {
    return fetchFn();
  }
  
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key) as Promise<T>;
  }
  
  const promise = fetchFn().finally(() => {
    pendingRequests.delete(key);
  });
  
  pendingRequests.set(key, promise);
  return promise;
}

// Generic fetch wrapper with auth and deduplication
async function fetchWithAuth(endpoint: string, options: Parameters<typeof fetch>[1] = {}) {
  return dedupeRequest(endpoint, options, async () => {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getToken();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        // Handle express-validator errors array format
        if (error.errors && Array.isArray(error.errors)) {
          const messages = error.errors.map((e: { msg?: string; message?: string }) => e.msg || e.message).filter(Boolean);
          throw new Error(messages.join('. ') || `HTTP ${response.status}`);
        }
        throw new Error(error.error || error.message || `HTTP ${response.status}`);
      }
      
      return response.json();
    } catch (err) {
      // Network errors (CORS, connection refused, etc.)
      if (err instanceof TypeError && err.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your connection.');
      }
      throw err;
    }
  });
}

// ==================== AUTH API ====================
export const authApi = {
  login: (email: string, password: string) =>
    fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  
  getMe: () => fetchWithAuth('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    fetchWithAuth('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// ==================== CLIENTS API ====================
export const clientsApi = {
  getAll: (params?: { search?: string; status?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return fetchWithAuth(`/clients?${queryParams.toString()}`);
  },
  
  getById: (id: number) => fetchWithAuth(`/clients/${id}`),
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Record<string, unknown>) =>
    fetchWithAuth(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    fetchWithAuth(`/clients/${id}`, {
      method: 'DELETE',
    }),

  resetAccessCode: (id: number) =>
    fetchWithAuth(`/clients/${id}/reset-access-code`, {
      method: 'POST',
    }),
};

// ==================== JOB CARDS API ====================
export const jobCardsApi = {
  getNextNumber: () => fetchWithAuth('/job-cards/next-number'),

  getAll: (params?: { status?: string; client_id?: number; search?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.client_id) queryParams.append('client_id', String(params.client_id));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return fetchWithAuth(`/job-cards?${queryParams.toString()}`);
  },
  
  getById: (id: number) => fetchWithAuth(`/job-cards/${id}`),
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/job-cards', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Record<string, unknown>) =>
    fetchWithAuth(`/job-cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  updateStatus: (id: number, status: string, note?: string) =>
    fetchWithAuth(`/job-cards/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, note }),
    }),
  
  delete: (id: number) =>
    fetchWithAuth(`/job-cards/${id}`, {
      method: 'DELETE',
    }),
};

// ==================== QUOTATIONS API ====================
export const quotationsApi = {
  getAll: (params?: { status?: string; client_id?: number; search?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.client_id) queryParams.append('client_id', String(params.client_id));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return fetchWithAuth(`/quotations?${queryParams.toString()}`);
  },
  
  getById: (id: number) => fetchWithAuth(`/quotations/${id}`),
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/quotations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Record<string, unknown>) =>
    fetchWithAuth(`/quotations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  updateStatus: (id: number, status: string, note?: string) =>
    fetchWithAuth(`/quotations/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, note }),
    }),
  
  convertToJob: (id: number) =>
    fetchWithAuth(`/quotations/${id}/convert`, {
      method: 'POST',
    }),

  convertToInvoice: (id: number) =>
    fetchWithAuth(`/quotations/${id}/convert-invoice`, {
      method: 'POST',
    }),
  
  delete: (id: number) =>
    fetchWithAuth(`/quotations/${id}`, {
      method: 'DELETE',
    }),
};

// ==================== INVOICES API ====================
export const invoicesApi = {
  getAll: (params?: { status?: string; client_id?: number; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.client_id) queryParams.append('client_id', String(params.client_id));
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return fetchWithAuth(`/invoices?${queryParams.toString()}`);
  },
  
  getById: (id: number) => fetchWithAuth(`/invoices/${id}`),
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateStatus: (id: number, status: string, paid_amount?: number, payment_ref?: string, paid_date?: string) =>
    fetchWithAuth(`/invoices/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, paid_amount, payment_ref, paid_date }),
    }),
  
  delete: (id: number) =>
    fetchWithAuth(`/invoices/${id}`, {
      method: 'DELETE',
    }),
};

// ==================== EMPLOYEES API ====================
export const employeesApi = {
  getAll: () => fetchWithAuth('/employees'),
  
  getById: (id: string | number) => fetchWithAuth(`/employees/${id}`),
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string | number, data: Record<string, unknown>) =>
    fetchWithAuth(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string | number) =>
    fetchWithAuth(`/employees/${id}`, {
      method: 'DELETE',
    }),

  uploadPhoto: (id: string | number, file: File) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('photo', file);
    
    const url = `${API_BASE_URL}/employees/${id}/photo`;
    
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    
    return fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      return r.json();
    });
  },
};

// ==================== DELIVERABLES API ====================
export const deliverablesApi = {
  getAll: (params?: { job_id?: number; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.job_id) queryParams.append('job_id', String(params.job_id));
    if (params?.status) queryParams.append('status', params.status);
    return fetchWithAuth(`/deliverables?${queryParams.toString()}`);
  },
  
  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/deliverables', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateStatus: (id: number, status: string) =>
    fetchWithAuth(`/deliverables/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  
  delete: (id: number) =>
    fetchWithAuth(`/deliverables/${id}`, {
      method: 'DELETE',
    }),
};

// ==================== FIELD TRACKING API ====================
export const fieldApi = {
  getSessions: () => fetchWithAuth('/field/sessions'),
  
  checkIn: (data: { user_id: string; job_id: number; lat: number; lng: number; address: string }) =>
    fetchWithAuth('/field/checkin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  checkOut: (sessionId: number, data: { lat: number; lng: number; address: string }) =>
    fetchWithAuth(`/field/sessions/${sessionId}/checkout`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ==================== NOTIFICATIONS API ====================
export const notificationsApi = {
  getAll: () => fetchWithAuth('/notifications'),
  
  markAsRead: (id: number) =>
    fetchWithAuth(`/notifications/${id}/read`, {
      method: 'PATCH',
    }),
  
  markAllAsRead: () =>
    fetchWithAuth('/notifications/read-all', {
      method: 'PATCH',
    }),
};

// ==================== DASHBOARD API ====================
export const dashboardApi = {
  getStats: () => fetchWithAuth('/dashboard/stats'),
};

// ==================== ADMIN API ====================
export const adminApi = {
  getUsers: () => fetchWithAuth('/admin/users'),
  
  createUser: (data: Record<string, unknown>) =>
    fetchWithAuth('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateUser: (id: string | number, data: Record<string, unknown>) =>
    fetchWithAuth(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  updateUserStatus: (id: number, status: string) =>
    fetchWithAuth(`/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  
  getRoles: () => fetchWithAuth('/admin/roles'),
  
  getAuditLogs: (page = 1, limit = 50) =>
    fetchWithAuth(`/admin/audit-logs?page=${page}&limit=${limit}`),

  flushAuditLogs: () =>
    fetchWithAuth('/admin/audit-logs', { method: 'DELETE' }),

  getSystemStats: () => fetchWithAuth('/admin/stats'),

  resetUserPassword: (userId: string | number, newPassword: string) =>
    fetchWithAuth(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  deleteUser: (userId: string | number) =>
    fetchWithAuth(`/admin/users/${userId}`, {
      method: 'DELETE',
    }),

  testDbConnection: () => fetchWithAuth('/admin/db-test'),

  getBackupSchedule: () => fetchWithAuth('/admin/backup-schedule'),

  saveBackupSchedule: (data: Record<string, string | boolean>) =>
    fetchWithAuth('/admin/backup-schedule', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ── Site Visit Requests ───────────────────────────────────────────────────
  getSiteVisits: (params?: { status?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status && params.status !== 'All') q.append('status', params.status);
    if (params?.page) q.append('page', String(params.page));
    if (params?.limit) q.append('limit', String(params.limit));
    return fetchWithAuth(`/admin/site-visits?${q.toString()}`);
  },

  confirmSiteVisit: (id: number, notes?: string) =>
    fetchWithAuth(`/admin/site-visits/${id}/confirm`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }),

  rejectSiteVisit: (id: number, rejection_reason: string) =>
    fetchWithAuth(`/admin/site-visits/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ rejection_reason }),
    }),

  completeSiteVisit: (id: number) =>
    fetchWithAuth(`/admin/site-visits/${id}/complete`, {
      method: 'PATCH',
    }),

  // ── Media Library ─────────────────────────────────────────────────────────
  getMedia: (params?: { category?: string; search?: string; page?: number }) => {
    const q = new URLSearchParams();
    if (params?.category && params.category !== 'all') q.append('category', params.category);
    if (params?.search) q.append('search', params.search);
    if (params?.page) q.append('page', String(params.page));
    return fetchWithAuth(`/admin/media?${q.toString()}`);
  },

  deleteMedia: (id: string) =>
    fetchWithAuth(`/admin/media/${id}`, { method: 'DELETE' }),
};

// ==================== FIELD TRACKING API (extended) ====================
export const fieldTrackingApi = {
  getSessions: (params?: { date?: string; user_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    return fetchWithAuth(`/field/sessions?${queryParams.toString()}`);
  },
  checkIn: (data: { user_id: string; job_id: number; lat: number; lng: number; address: string; notes?: string }) =>
    fetchWithAuth('/field/checkin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  checkOut: (sessionId: number, data: { lat: number; lng: number; address: string }) =>
    fetchWithAuth(`/field/sessions/${sessionId}/checkout`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ==================== SETTINGS API ====================
export const settingsApi = {
  getCompanyProfile: () => fetchWithAuth('/settings/company'),

  updateCompanyProfile: (data: Record<string, unknown>) =>
    fetchWithAuth('/settings/company', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getUserProfile: () => fetchWithAuth('/settings/profile'),

  updateUserProfile: (data: Record<string, unknown>) =>
    fetchWithAuth('/settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  uploadAvatar: (file: File) => {
    const token = getToken();
    if (!token) {
      return Promise.reject(new Error('Not authenticated'));
    }
    
    const formData = new FormData();
    formData.append('avatar', file);
    
    const url = `${API_BASE_URL}/settings/profile/avatar`;
    
    // Validate URL before making request
    if (!url.startsWith('http') && !url.startsWith('/')) {
      console.error('[uploadAvatar] Invalid URL constructed:', url);
      console.error('[uploadAvatar] API_BASE_URL was:', API_BASE_URL);
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    
    console.log('[uploadAvatar] Uploading to:', url);
    
    return fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }).then(async (r) => {
      console.log('[uploadAvatar] Response status:', r.status);
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
        console.error('[uploadAvatar] Error response:', err);
        throw new Error(err.error || err.message || `HTTP ${r.status}`);
      }
      return r.json();
    }).catch((err) => {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        throw new Error('Network error - cannot connect to server');
      }
      throw err;
    });
  },

  getNotificationPrefs: () => fetchWithAuth('/settings/notifications'),

  updateNotificationPrefs: (data: Record<string, unknown>) =>
    fetchWithAuth('/settings/notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ── Site / Branding settings ──────────────────────────────────────────────
  getSiteSettings: () => fetchWithAuth(`/settings/site?_t=${Date.now()}`),
  
  // Debug: Check raw DB values
  debugSiteSettings: () => fetchWithAuth('/settings/debug-site-settings'),

  updateSiteSettings: (data: Record<string, string>) =>
    fetchWithAuth('/settings/site', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getModuleSettings: () => fetchWithAuth('/settings/modules'),

  updateModuleSettings: (data: Record<string, boolean>) =>
    fetchWithAuth('/settings/modules', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ── Role Permissions (stored in DB via settings table) ───────────────────
  getRolePermissions: () => fetchWithAuth('/settings/role-permissions'),

  saveRolePermissions: (data: Record<string, Record<string, string[]>>) =>
    fetchWithAuth('/settings/role-permissions', {
      method: 'PUT',
      body: JSON.stringify({ permissions: data }),
    }),

  // ── Session Timeout (stored in DB via settings table) ────────────────────
  getSessionTimeout: () => fetchWithAuth('/settings/session-timeout'),

  saveSessionTimeout: (timeoutMin: number, warningMin: number) =>
    fetchWithAuth('/settings/session-timeout', {
      method: 'PUT',
      body: JSON.stringify({ timeout_minutes: timeoutMin, warning_minutes: warningMin }),
    }),

  // ── Security Policy (login attempts, password rules, IP whitelist) ────────
  getSecurityPolicy: () => fetchWithAuth('/settings/security-policy'),

  saveSecurityPolicy: (data: Record<string, string | boolean | number>) =>
    fetchWithAuth('/settings/security-policy', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  uploadBrandingFile: (formData: FormData) => {
    const token = getToken();
    const url = `${API_BASE_URL}/settings/site/upload`;
    
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    
    return fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      return r.json();
    });
  },

  // ── SMTP Configuration ────────────────────────────────────────────────────
  getSmtpConfig: () => fetchWithAuth('/settings/smtp'),

  saveSmtpConfig: (data: Record<string, string | boolean>) =>
    fetchWithAuth('/settings/smtp', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  testSmtpEmail: (to: string) =>
    fetchWithAuth('/settings/smtp/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),

  // ── Notification Config ───────────────────────────────────────────────────
  getNotificationConfig: () => fetchWithAuth('/settings/notification-config'),

  saveNotificationConfig: (config: unknown) =>
    fetchWithAuth('/settings/notification-config', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }),

  // ── Media Library ─────────────────────────────────────────────────────────
  uploadMediaFile: (formData: FormData) => {
    const token = getToken();
    const url = `${API_BASE_URL}/settings/media/upload`;
    
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    
    return fetch(url, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      return r.json();
    });
  },
};

// ==================== PAYMENTS API ====================
export const paymentsApi = {
  getAll: (params?: { status?: string; client_id?: number; search?: string; page?: number; limit?: number; from_date?: string; to_date?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.client_id) queryParams.append('client_id', String(params.client_id));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    return fetchWithAuth(`/payments?${queryParams.toString()}`);
  },

  getById: (id: number) => fetchWithAuth(`/payments/${id}`),

  create: (data: Record<string, unknown>) =>
    fetchWithAuth('/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Record<string, unknown>) =>
    fetchWithAuth(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchWithAuth(`/payments/${id}`, {
      method: 'DELETE',
    }),

  getSummary: () => fetchWithAuth('/payments/summary'),
};

// ==================== REPORTS API ====================
export const reportsApi = {
  getSummary: (period?: string) => {
    const queryParams = new URLSearchParams();
    if (period) queryParams.append('period', period);
    return fetchWithAuth(`/reports/summary?${queryParams.toString()}`);
  },

  getRevenue: (period?: string) => {
    const queryParams = new URLSearchParams();
    if (period) queryParams.append('period', period);
    return fetchWithAuth(`/reports/revenue?${queryParams.toString()}`);
  },

  getJobCompletion: (period?: string) => {
    const queryParams = new URLSearchParams();
    if (period) queryParams.append('period', period);
    return fetchWithAuth(`/reports/jobs?${queryParams.toString()}`);
  },

  getEmployeeUtilization: () => fetchWithAuth('/reports/employees'),

  getClientAnalysis: () => fetchWithAuth('/reports/clients'),

  getServiceBreakdown: () => fetchWithAuth('/reports/services'),
};

// ==================== CLIENT PORTAL API ====================
export const clientPortalApi = {
  login: (accessCode: string, email: string) =>
    fetchWithAuth('/reports/portal/login', {
      method: 'POST',
      body: JSON.stringify({ access_code: accessCode, email }),
    }),

  submitSiteVisit: (data: Record<string, string>) => {
    const url = `${API_BASE_URL}/site-visits`;
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(async (r) => {
      const json = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
      return json;
    });
  },

  getJobs: (token: string) => {
    const url = `${API_BASE_URL}/reports/portal/jobs`;
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    return fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.json());
  },

  getJobDetail: (jobId: string, token: string) => {
    const url = `${API_BASE_URL}/reports/portal/jobs/${jobId}`;
    if (!url.startsWith('http') && !url.startsWith('/')) {
      return Promise.reject(new Error('Invalid API URL configuration'));
    }
    return fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.json());
  },
};

export default {
  auth: authApi,
  clients: clientsApi,
  jobCards: jobCardsApi,
  quotations: quotationsApi,
  invoices: invoicesApi,
  employees: employeesApi,
  deliverables: deliverablesApi,
  field: fieldApi,
  notifications: notificationsApi,
  dashboard: dashboardApi,
  admin: adminApi,
  settings: settingsApi,
  reports: reportsApi,
  clientPortal: clientPortalApi,
};
